/**
 * GWP Cart Monitor
 * Automatically removes GWP items from cart when subtotal falls below threshold
 * @description Monitors cart changes and removes free gift items when cart no longer qualifies
 */

class GwpCartMonitor {
  constructor() {
    this.widget = null;
    this.threshold = 0;
    this.gwpVariantIds = [];
    this.isProcessing = false;
    this.isInitialized = false;

    this.init();
  }

  /**
   * Initialize the monitor
   */
  init() {
    // Wait for custom element to be defined
    if (customElements.get("gwp-float-widget")) {
      this.waitForWidget();
    } else {
      // Wait for custom element registration
      customElements.whenDefined("gwp-float-widget").then(() => {
        this.waitForWidget();
      });
    }
  }

  /**
   * Wait for widget element to appear in DOM and be fully initialized
   */
  waitForWidget() {
    const checkWidget = () => {
      this.widget = document.querySelector("gwp-float-widget");

      if (!this.widget) {
        // Widget not in DOM yet, try again
        setTimeout(checkWidget, 100);
        return;
      }

      // Widget found, wait a bit more for it to fully initialize
      // The widget needs to fetch cart data and set up its internal state
      setTimeout(() => {
        this.setup();
      }, 500);
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", checkWidget);
    } else {
      checkWidget();
    }
  }

  /**
   * Setup the monitor after widget is ready
   */
  setup() {
    if (this.isInitialized) {
      return;
    }

    if (!this.widget) {
      console.log(
        "[GWP Cart Monitor] No widget found, skipping initialization"
      );
      return;
    }

    // Get threshold and GWP variant IDs from widget
    this.threshold = parseInt(this.widget.dataset.cartThreshold, 10) || 0;
    this.gwpVariantIds = this.getGwpVariantIds();

    console.log("[GWP Cart Monitor] Initialized", {
      threshold: this.threshold,
      thresholdFormatted: this.formatMoney(this.threshold),
      gwpVariantIds: this.gwpVariantIds,
    });

    if (this.threshold === 0 || this.gwpVariantIds.length === 0) {
      console.log("[GWP Cart Monitor] Invalid configuration, skipping");
      return;
    }

    this.isInitialized = true;

    // Listen for cart updates
    this.attachCartListeners();

    // Check cart on initial load after a short delay
    setTimeout(() => {
      this.checkCart();
    }, 1000);
  }

  /**
   * Get all GWP variant IDs from the widget
   * @returns {number[]} Array of variant IDs
   */
  getGwpVariantIds() {
    const products = this.widget.querySelectorAll("[data-variant-id]");
    return Array.from(products)
      .map((product) => {
        return parseInt(product.dataset.variantId, 10);
      })
      .filter((id) => !isNaN(id));
  }

  /**
   * Attach listeners for cart updates
   */
  attachCartListeners() {
    // Listen for custom cart update events (common in Shopify themes)
    document.addEventListener("cart:updated", () => this.checkCart());
    document.addEventListener("cart:refresh", () => this.checkCart());

    // Listen for theme-specific events
    document.addEventListener("theme:cart:change", () => this.checkCart());

    // Fallback: periodically check cart when page is visible
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) {
        this.checkCart();
      }
    });
  }

  /**
   * Check cart and remove GWP items if below threshold
   */
  async checkCart() {
    if (this.isProcessing) {
      console.log("[GWP Cart Monitor] Already processing, skipping check");
      return;
    }

    try {
      this.isProcessing = true;

      const cart = await this.fetchCart();

      if (!cart) {
        console.log("[GWP Cart Monitor] No cart data received");
        return;
      }

      // Cart total_price is in cents
      const cartSubtotal = cart.total_price;

      console.log("[GWP Cart Monitor] Cart check:", {
        cartSubtotal: cartSubtotal,
        cartSubtotalFormatted: this.formatMoney(cartSubtotal),
        threshold: this.threshold,
        thresholdFormatted: this.formatMoney(this.threshold),
        isBelowThreshold: cartSubtotal < this.threshold,
        itemCount: cart.items.length,
      });

      // If cart is below threshold, remove all GWP items
      if (cartSubtotal < this.threshold) {
        console.log(
          "[GWP Cart Monitor] Cart is below threshold, checking for GWP items to remove"
        );
        await this.removeGwpItems(cart);
      } else {
        console.log(
          "[GWP Cart Monitor] Cart meets threshold, no action needed"
        );
      }
    } catch (error) {
      console.error("[GWP Cart Monitor] Error checking cart:", error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Fetch current cart data from Shopify
   * @returns {Promise<Object>} Cart object
   */
  async fetchCart() {
    try {
      const response = await fetch("/cart.js", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Cart fetch failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("[GWP Cart Monitor] Error fetching cart:", error);
      return null;
    }
  }

  /**
   * Remove GWP items from cart
   * @param {Object} cart - Current cart object
   */
  async removeGwpItems(cart) {
    const itemsToRemove = cart.items.filter((item) => {
      return this.gwpVariantIds.includes(item.variant_id);
    });

    console.log("[GWP Cart Monitor] Found GWP items in cart:", {
      totalItems: cart.items.length,
      gwpItemsFound: itemsToRemove.length,
      itemsToRemove: itemsToRemove.map((item) => ({
        variantId: item.variant_id,
        title: item.title,
        quantity: item.quantity,
        key: item.key,
      })),
    });

    if (itemsToRemove.length === 0) {
      console.log("[GWP Cart Monitor] No GWP items to remove");
      return;
    }

    // Remove items one by one using /cart/change.js
    // This is more reliable than bulk updates
    for (const item of itemsToRemove) {
      const lineIndex = cart.items.indexOf(item) + 1;

      console.log("[GWP Cart Monitor] Removing item:", {
        title: item.title,
        variantId: item.variant_id,
        lineIndex: lineIndex,
        key: item.key,
      });

      try {
        const response = await fetch("/cart/change.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            line: lineIndex,
            quantity: 0,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("[GWP Cart Monitor] Failed to remove item:", {
            status: response.status,
            error: errorText,
            item: item.title,
          });
          continue;
        }

        const updatedCart = await response.json();
        console.log("[GWP Cart Monitor] Successfully removed:", item.title);

        // Update the cart reference for next iteration
        cart = updatedCart;
      } catch (error) {
        console.error(
          "[GWP Cart Monitor] Error removing item:",
          item.title,
          error
        );
      }
    }

    console.log("[GWP Cart Monitor] Finished removing all GWP items");

    // Dispatch event to notify other components
    document.dispatchEvent(
      new CustomEvent("gwp:removed", {
        detail: {
          removedItems: itemsToRemove,
        },
      })
    );

    // Trigger cart refresh events for theme
    // cart:build is the main event that rebuilds the cart drawer
    document.dispatchEvent(new CustomEvent("cart:build"));
    document.dispatchEvent(new CustomEvent("cart:updated"));
    document.dispatchEvent(new CustomEvent("cart:refresh"));
  }

  /**
   * Format money amount
   * @param {number} cents - Amount in cents
   * @returns {string} Formatted money string
   */
  formatMoney(cents) {
    const dollars = cents / 100;
    return `$${dollars.toFixed(2)}`;
  }
}

// Initialize monitor when script loads
console.log("[GWP Cart Monitor] Script loaded, initializing...");
new GwpCartMonitor();
